import { memo } from "react";
import {
  Avatar,
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { GfFlyerReviewComment } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { Author } from "./useAuthorResolver";
import { absoluteTime, relativeFromNow } from "./time";
import { COMMENT_TARGET, commentTargetOf, isResolvedComment } from "./commentTypes";
import { reviewTokens } from "../../theme";

interface Props {
  comment: GfFlyerReviewComment;
  author: Author;
  resolvedBy?: Author;
  variant: "root" | "reply";
}

const TargetChip = ({ comment }: { comment: GfFlyerReviewComment }) => {
  const { t } = useTranslation();
  const targetType = commentTargetOf(comment);

  if (targetType === COMMENT_TARGET.FIELD) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={t(messages.Comment_On_Field(), { key: comment.fieldKey || "" })}
      />
    );
  }
  if (targetType === COMMENT_TARGET.FLYER_ITEM) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={t(messages.Comment_On_Flyer_Item())}
      />
    );
  }
  if (targetType === COMMENT_TARGET.FLYER) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={t(messages.Comment_On_Flyer())}
      />
    );
  }
  return null;
};

export const CommentCard = memo(function CommentCard({
  comment,
  author,
  resolvedBy,
  variant,
}: Props) {
  const { t } = useTranslation();
  const isRoot = variant === "root";
  const isResolved = isResolvedComment(comment);

  const avatarBg = author.isAdmin
    ? reviewTokens.brand
    : author.isAnonymous
      ? "#C2410C"
      : reviewTokens.slate500;

  const body = (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Avatar
        sx={{
          width: isRoot ? 30 : 24,
          height: isRoot ? 30 : 24,
          fontSize: isRoot ? 12 : 10,
          backgroundColor: avatarBg,
          color: "#fff",
        }}
      >
        {author.initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ rowGap: 0.5 }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {author.name}
          </Typography>
          <Chip
            size="small"
            label={
              author.isAdmin ? t(messages.Admin()) : t(messages.Reviewer())
            }
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 500,
              bgcolor: author.isAdmin
                ? reviewTokens.brandSoft
                : reviewTokens.slate100,
              color: author.isAdmin
                ? reviewTokens.brandHover
                : reviewTokens.slate600,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
          <Tooltip title={absoluteTime(comment.createdAt)}>
            <Typography
              variant="caption"
              sx={{ color: "text.disabled" }}
            >
              · {relativeFromNow(comment.createdAt)}
            </Typography>
          </Tooltip>
          {isRoot && <TargetChip comment={comment} />}
          {isRoot && (
            <Chip
              size="small"
              label={
                isResolved
                  ? t(messages.Comments_Resolved())
                  : t(messages.Comments_Open())
              }
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 500,
                bgcolor: isResolved
                  ? "rgba(22, 163, 74, 0.12)"
                  : "rgba(14, 165, 233, 0.12)",
                color: isResolved ? reviewTokens.success : reviewTokens.info,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}
        </Stack>

        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            mt: 0.75,
            color: "text.primary",
            lineHeight: 1.55,
          }}
        >
          {comment.message || ""}
        </Typography>

        {isRoot && isResolved && resolvedBy && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              mt: 1,
              pt: 0.75,
              borderTop: `1px dashed ${reviewTokens.border}`,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t(messages.Resolved_By(), { name: resolvedBy.name })}
            </Typography>
            {comment.resolvedAt && (
              <Tooltip title={absoluteTime(comment.resolvedAt)}>
                <Typography variant="caption" color="text.secondary">
                  · {relativeFromNow(comment.resolvedAt)}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
    </Stack>
  );

  if (isRoot) {
    return (
      <Box
        sx={{
          py: 1.25,
          opacity: isResolved ? 0.75 : 1,
        }}
        data-testid={`comment-${comment.id}`}
        data-status={comment.status}
        id={`comment-${comment.id}`}
      >
        {body}
      </Box>
    );
  }

  return (
    <Box
      sx={{ py: 0.75 }}
      data-testid={`comment-${comment.id}`}
      data-status={comment.status}
      id={`comment-${comment.id}`}
    >
      {body}
    </Box>
  );
});
